/**
 * Racket Quiz — "Find Your Perfect Racket"
 * Multi-step interactive quiz that recommends padel rackets
 * based on user preferences using inline catalog JSON.
 *
 * Vanilla JS, no dependencies. Accessible (keyboard + screen reader).
 */
(function () {
  'use strict';

  /* ------------------------------------------------
     Constants
     ------------------------------------------------ */
  var TOTAL_STEPS = 4; // question steps (0-3), step 4 = results
  var AUTO_ADVANCE_DELAY = 300; // ms after selection before advancing
  var MAX_CARDS = 6;
  var MAX_TOTAL_RESULTS = 10;

  /* ------------------------------------------------
     State
     ------------------------------------------------ */
  var currentStep = 0;
  var answers = {
    level: null,      // Beginner | Intermediate | Advanced
    style: null,      // Power | Control | Balanced | Defensive
    shape: null,      // Round | Diamond | Teardrop | No Preference
    budget: null      // value string e.g. "under-5000"
  };
  var budgetRange = { min: 0, max: 999999 };
  var isTransitioning = false;
  var rackets = [];   // Loaded from inline JSON on init

  /* ------------------------------------------------
     DOM References
     ------------------------------------------------ */
  var quiz = document.querySelector('[data-racket-quiz]');
  if (!quiz) return;

  var progressFill = quiz.querySelector('[data-quiz-progress-fill]');
  var progressBar = quiz.querySelector('[data-quiz-progress]');
  var dotsContainer = quiz.querySelector('[data-quiz-dots]');
  var liveRegion = quiz.querySelector('[data-quiz-live-region]');
  var stepContainer = quiz.querySelector('[data-quiz-step-container]');
  var steps = quiz.querySelectorAll('[data-quiz-step]');
  var resultsContainer = quiz.querySelector('[data-quiz-results]');
  var allOptions = quiz.querySelectorAll('[data-quiz-option]');
  var allBackBtns = quiz.querySelectorAll('[data-quiz-back]');

  /* ------------------------------------------------
     Initialization
     ------------------------------------------------ */
  function init() {
    loadRacketsFromJSON();
    bindOptionClicks();
    bindBackButtons();
    bindKeyboardNav();
    updateProgress();
    updateStepContainerHeight();
  }

  function loadRacketsFromJSON() {
    // Read from static dataset bundled by scripts/refresh-quiz-data.js
    if (Array.isArray(window.LATIENDA_RACKETS)) {
      rackets = window.LATIENDA_RACKETS;
      return;
    }
    // Fallback: legacy inline JSON script tag (kept for safety during deploy transitions)
    var scriptTag = document.querySelector('script[data-quiz-rackets]');
    if (!scriptTag) {
      rackets = [];
      return;
    }
    try {
      rackets = JSON.parse(scriptTag.textContent) || [];
    } catch (e) {
      rackets = [];
    }
  }

  /* ------------------------------------------------
     Event Binding
     ------------------------------------------------ */
  function bindOptionClicks() {
    allOptions.forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (isTransitioning) return;
        var step = parseInt(btn.getAttribute('data-step'), 10);
        var value = btn.getAttribute('data-value');
        selectOption(step, value, btn);
      });
    });
  }

  function bindBackButtons() {
    allBackBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (isTransitioning) return;
        goToPreviousStep();
      });
    });
  }

  function bindKeyboardNav() {
    quiz.addEventListener('keydown', function (e) {
      if (isTransitioning) return;
      var activeStep = quiz.querySelector('.racket-quiz__step--active');
      if (!activeStep) return;

      var options = activeStep.querySelectorAll('[data-quiz-option]');
      if (options.length === 0) return;

      var focused = document.activeElement;
      var focusedIndex = Array.prototype.indexOf.call(options, focused);

      switch (e.key) {
        case 'ArrowDown':
        case 'ArrowRight':
          e.preventDefault();
          if (focusedIndex < options.length - 1) {
            options[focusedIndex + 1].focus();
            options[focusedIndex + 1].setAttribute('tabindex', '0');
            if (focusedIndex >= 0) options[focusedIndex].setAttribute('tabindex', '-1');
          }
          break;
        case 'ArrowUp':
        case 'ArrowLeft':
          e.preventDefault();
          if (focusedIndex > 0) {
            options[focusedIndex - 1].focus();
            options[focusedIndex - 1].setAttribute('tabindex', '0');
            if (focusedIndex >= 0) options[focusedIndex].setAttribute('tabindex', '-1');
          }
          break;
        case 'Enter':
        case ' ':
          if (focused && focused.hasAttribute('data-quiz-option')) {
            e.preventDefault();
            focused.click();
          }
          break;
      }
    });
  }

  /* ------------------------------------------------
     Option Selection
     ------------------------------------------------ */
  function selectOption(step, value, btn) {
    switch (step) {
      case 0: answers.level = value; break;
      case 1: answers.style = value; break;
      case 2: answers.shape = value; break;
      case 3:
        answers.budget = value;
        budgetRange.min = parseInt(btn.getAttribute('data-min'), 10) || 0;
        budgetRange.max = parseInt(btn.getAttribute('data-max'), 10) || 999999;
        break;
    }

    var stepEl = quiz.querySelector('[data-quiz-step="' + step + '"]');
    var stepOptions = stepEl.querySelectorAll('[data-quiz-option]');
    stepOptions.forEach(function (opt) {
      opt.classList.remove('racket-quiz__option--selected');
      opt.setAttribute('aria-checked', 'false');
    });
    btn.classList.add('racket-quiz__option--selected');
    btn.setAttribute('aria-checked', 'true');

    setTimeout(function () {
      if (step < TOTAL_STEPS - 1) {
        goToStep(step + 1);
      } else {
        goToStep(TOTAL_STEPS);
      }
    }, AUTO_ADVANCE_DELAY);
  }

  /* ------------------------------------------------
     Step Navigation
     ------------------------------------------------ */
  function goToStep(targetStep) {
    if (isTransitioning) return;
    isTransitioning = true;

    var currentEl = quiz.querySelector('.racket-quiz__step--active');
    var targetEl = quiz.querySelector('[data-quiz-step="' + targetStep + '"]');
    if (!currentEl || !targetEl) { isTransitioning = false; return; }

    var goingForward = targetStep > currentStep;

    currentEl.classList.remove('racket-quiz__step--active');
    currentEl.classList.add(goingForward ? 'racket-quiz__step--exit-left' : 'racket-quiz__step--enter-right');
    targetEl.classList.add(goingForward ? 'racket-quiz__step--enter-right' : 'racket-quiz__step--exit-left');

    void targetEl.offsetWidth;

    targetEl.classList.remove('racket-quiz__step--enter-right', 'racket-quiz__step--exit-left');
    targetEl.classList.add('racket-quiz__step--active');

    currentStep = targetStep;
    updateProgress();
    updateStepContainerHeight();

    if (targetStep < TOTAL_STEPS) {
      announceToScreenReader('Step ' + (targetStep + 1) + ' of ' + TOTAL_STEPS);
    } else {
      announceToScreenReader('Quiz complete. Loading your recommendations.');
    }

    setTimeout(function () {
      currentEl.classList.remove('racket-quiz__step--exit-left', 'racket-quiz__step--enter-right');

      if (targetStep < TOTAL_STEPS) {
        var firstOption = targetEl.querySelector('[data-quiz-option]');
        if (firstOption) firstOption.focus();
      }

      isTransitioning = false;

      if (targetStep === TOTAL_STEPS) {
        showResults();
      }
    }, 400);
  }

  function goToPreviousStep() {
    if (currentStep > 0) {
      goToStep(currentStep - 1);
    }
  }

  /* ------------------------------------------------
     Progress Bar
     ------------------------------------------------ */
  function updateProgress() {
    var displayStep = Math.min(currentStep, TOTAL_STEPS - 1);
    var pct = ((displayStep + 1) / TOTAL_STEPS) * 100;
    if (currentStep >= TOTAL_STEPS) pct = 100;

    progressFill.style.width = pct + '%';
    progressBar.setAttribute('aria-valuenow', Math.min(currentStep + 1, TOTAL_STEPS));

    var dots = dotsContainer.querySelectorAll('.racket-quiz__step-dot');
    dots.forEach(function (dot, i) {
      dot.classList.remove('racket-quiz__step-dot--active', 'racket-quiz__step-dot--completed');
      if (i === displayStep && currentStep < TOTAL_STEPS) {
        dot.classList.add('racket-quiz__step-dot--active');
      } else if (i < displayStep || currentStep >= TOTAL_STEPS) {
        dot.classList.add('racket-quiz__step-dot--completed');
      }
    });

    var progressEl = quiz.querySelector('.racket-quiz__progress');
    if (currentStep >= TOTAL_STEPS) {
      progressEl.style.opacity = '0';
      progressEl.style.transition = 'opacity 300ms ease';
    } else {
      progressEl.style.opacity = '1';
    }
  }

  /* ------------------------------------------------
     Dynamic Step Height
     ------------------------------------------------ */
  function updateStepContainerHeight() {
    var activeStep = quiz.querySelector('.racket-quiz__step--active');
    if (activeStep) {
      stepContainer.style.minHeight = '';
      requestAnimationFrame(function () {
        stepContainer.style.minHeight = activeStep.scrollHeight + 'px';
      });
    }
  }

  /* ------------------------------------------------
     Screen Reader Announcements
     ------------------------------------------------ */
  function announceToScreenReader(message) {
    if (liveRegion) {
      liveRegion.textContent = message;
    }
  }

  /* ------------------------------------------------
     Results
     ------------------------------------------------ */
  function showResults() {
    resultsContainer.innerHTML = buildLoadingHTML();

    requestAnimationFrame(function () {
      var match = filterRackets();
      var recText = buildRecommendationText();
      var recHeading = 'Your Perfect Match';

      if (!match.results || match.results.length === 0) {
        resultsContainer.innerHTML = buildNoResultsHTML(recText);
      } else {
        resultsContainer.innerHTML = buildResultsHTML(recHeading, recText, match.results, match.tier);
      }

      bindRetakeButton();
      updateStepContainerHeight();

      announceToScreenReader('Quiz results loaded. ' + match.results.length + ' rackets recommended.');
    });
  }

  /* ------------------------------------------------
     Filter Logic — 5-tier priority fallback
     Returns the FIRST tier that has any matches.
     { results: [...], tier: 1-5, relaxed: 'shape' | 'style' | 'budget' | null }
     ------------------------------------------------ */
  function filterRackets() {
    if (!rackets || rackets.length === 0) return { results: [], tier: 0, relaxed: null };

    var levelFilter = answers.level;
    var styleFilter = answers.style;
    var shapeFilter = answers.shape;
    var skipShape = (shapeFilter === 'No Preference');

    function inBudget(r) {
      var price = parseFloat(r.price) || 0;
      return price >= budgetRange.min && price <= budgetRange.max;
    }
    // OR-match against the racket's suitable_levels list (multi-level
    // rackets fit more than one bucket). Falls back to the single
    // racket.level value if suitable_levels is missing or empty so
    // legacy / not-yet-tagged rackets keep working.
    function levelMatch(r) {
      var list = r.suitable_levels;
      if (Array.isArray(list) && list.length > 0) {
        return list.indexOf(levelFilter) !== -1;
      }
      return r.level === levelFilter;
    }
    function styleMatch(r) { return r.style === styleFilter; }
    function shapeMatch(r) {
      if (skipShape) return true;
      if (shapeFilter === 'Teardrop') return (r.shape === 'Teardrop' || r.shape === 'Hybrid');
      return r.shape === shapeFilter;
    }
    function isAvailable(r) { return r.available === true; }

    // Try each tier in order; return the first non-empty one
    var tier1 = rackets.filter(function (r) {
      return isAvailable(r) && levelMatch(r) && styleMatch(r) && shapeMatch(r) && inBudget(r);
    });
    if (tier1.length > 0) return { results: tier1.slice(0, MAX_TOTAL_RESULTS), tier: 1, relaxed: null };

    if (!skipShape) {
      var tier2 = rackets.filter(function (r) {
        return isAvailable(r) && levelMatch(r) && styleMatch(r) && inBudget(r);
      });
      if (tier2.length > 0) return { results: tier2.slice(0, MAX_TOTAL_RESULTS), tier: 2, relaxed: 'shape' };
    }

    var tier3 = rackets.filter(function (r) {
      return isAvailable(r) && levelMatch(r) && shapeMatch(r) && inBudget(r);
    });
    if (tier3.length > 0) return { results: tier3.slice(0, MAX_TOTAL_RESULTS), tier: 3, relaxed: 'style' };

    var tier4 = rackets.filter(function (r) {
      return isAvailable(r) && levelMatch(r) && inBudget(r);
    });
    if (tier4.length > 0) return { results: tier4.slice(0, MAX_TOTAL_RESULTS), tier: 4, relaxed: 'shape and style' };

    var tier5 = rackets.filter(function (r) {
      return isAvailable(r) && levelMatch(r);
    });
    if (tier5.length > 0) return { results: tier5.slice(0, MAX_TOTAL_RESULTS), tier: 5, relaxed: 'budget, shape, and style' };

    return { results: [], tier: 0, relaxed: null };
  }

  /* ------------------------------------------------
     Builds a banner explaining what was relaxed (for tiers 2-5)
     ------------------------------------------------ */
  function buildRelaxedBannerHTML(tier, relaxed) {
    if (tier === 1 || !relaxed) return '';
    var msg;
    switch (tier) {
      case 2: msg = 'We didn\'t find an exact match. These rackets fit your level, style, and budget — but have a different shape than you preferred.'; break;
      case 3: msg = 'We didn\'t find an exact match. These rackets fit your level, shape, and budget — but have a different play style than you preferred.'; break;
      case 4: msg = 'We didn\'t find an exact match for your shape and style. Here are rackets at your level within your budget.'; break;
      case 5: msg = 'We don\'t have rackets in your budget range matching your other preferences. Here are options at your level — you may need to adjust your budget.'; break;
      default: return '';
    }
    return '<div class="racket-quiz__relaxed-banner" role="status">'
      + '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">'
      + '<circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>'
      + '</svg>'
      + '<span>' + escapeHtml(msg) + '</span>'
      + '</div>';
  }

  /* ------------------------------------------------
     Recommendation Text Builder

     Builds the personalized blurb on the results screen. Hardened so that if
     any answer is missing (e.g. an in-flight click or storage hiccup) the copy
     drops the affected clause cleanly instead of silently substituting a
     wrong default. Never lie to the user about what they picked.
     ------------------------------------------------ */
  function buildRecommendationText() {
    var level = answers.level || null;
    var style = answers.style || null;
    var shape = answers.shape || null;

    /* Resolve a concrete shape word to mention. Use the user's pick if they
       gave one; if "No Preference" or absent, derive from style; otherwise
       leave shapeRec null and the sentence drops the shape clause. */
    var shapeRec = null;
    if (shape === 'Round') shapeRec = 'round';
    else if (shape === 'Diamond') shapeRec = 'diamond';
    else if (shape === 'Teardrop') shapeRec = 'teardrop-shaped';
    else if (shape === 'Hybrid') shapeRec = 'hybrid';
    else if (style) {
      switch (style) {
        case 'Power': shapeRec = 'diamond'; break;
        case 'Control': shapeRec = 'round'; break;
        case 'Defensive': shapeRec = 'round'; break;
        case 'Balanced': shapeRec = 'teardrop-shaped'; break;
      }
    }

    var styleText = style ? style.toLowerCase() : null;
    var shapeNoun = shapeRec ? 'a ' + shapeRec + ' racket' : 'a racket';

    if (level === 'Beginner') {
      return 'As a beginner, you\'ll benefit from a forgiving racket with a large sweet spot. We recommend ' + shapeNoun + ' that\'s easy to handle and helps you build confidence on the court.';
    }
    if (level === 'Intermediate') {
      if (styleText) {
        return 'As an intermediate player who loves ' + styleText + ' play, you need ' + shapeNoun + ' that matches your developing skills and helps you take your game to the next level.';
      }
      return 'As an intermediate player, you need ' + shapeNoun + ' that matches your developing skills and helps you take your game to the next level.';
    }
    if (level === 'Advanced') {
      if (styleText) {
        return 'As an advanced player with a ' + styleText + ' style, ' + shapeNoun + ' with responsive materials will give you the precision and performance you demand.';
      }
      return 'As an advanced player, ' + shapeNoun + ' with responsive materials will give you the precision and performance you demand.';
    }
    /* Level somehow not captured — give a sensible blurb that still respects
       whichever answer survived. */
    if (styleText) {
      return 'For your ' + styleText + ' play style, we recommend ' + shapeNoun + ' that gives you confidence and control on the court.';
    }
    return 'Based on your answers, ' + shapeNoun + ' is the best fit for your game.';
  }

  /* ------------------------------------------------
     Money Formatter
     ------------------------------------------------ */
  function formatMoney(price) {
    var p = parseFloat(price) || 0;
    var formatted = Math.round(p).toLocaleString('en-EG');
    return 'EGP ' + formatted;
  }

  /* ------------------------------------------------
     HTML Builders
     ------------------------------------------------ */
  function buildLoadingHTML() {
    return '<div class="racket-quiz__loading">'
      + '<div class="racket-quiz__loading-spinner"></div>'
      + '<p class="racket-quiz__loading-text">Finding your perfect rackets...</p>'
      + '</div>';
  }

  /* ------------------------------------------------
     Guide Link Builder

     "Read the full guide" link on the results screen, pointing at the
     best-of article for the level the user picked. URL and label arrive
     pre-localized via data-guide-* attributes on the results container
     (set in racket-quiz.liquid) — this file has no access to translations.
     Returns '' when the level has no mapped guide or attributes are absent,
     so older cached markup degrades to no link rather than a broken one.
     ------------------------------------------------ */
  function buildGuideLinkHTML() {
    var level = (answers.level || '').toLowerCase();
    if (level !== 'beginner' && level !== 'intermediate' && level !== 'advanced') return '';
    var ds = resultsContainer.dataset || {};
    var cap = level.charAt(0).toUpperCase() + level.slice(1);
    var url = ds['guideUrl' + cap];
    var title = ds['guideTitle' + cap];
    if (!url || !title) return '';
    var prompt = ds.guidePrompt || '';
    return '<div class="racket-quiz__guide-link">'
      + (prompt ? '<span class="racket-quiz__guide-link-prompt">' + escapeHtml(prompt) + '</span> ' : '')
      + '<a href="' + escapeAttr(url) + '">' + escapeHtml(title) + '</a>'
      + '</div>';
  }

  function buildResultsHTML(heading, text, products, tier) {
    var cards = products.slice(0, MAX_CARDS);
    var extras = products.slice(MAX_CARDS, MAX_TOTAL_RESULTS);
    var relaxedKey;
    switch (tier) {
      case 2: relaxedKey = 'shape'; break;
      case 3: relaxedKey = 'style'; break;
      case 4: relaxedKey = 'shape and style'; break;
      case 5: relaxedKey = 'budget, shape, and style'; break;
      default: relaxedKey = null;
    }

    // For tiers 2+, soften heading from "Your Perfect Match" to "Closest Matches"
    if (tier && tier > 1) {
      heading = 'Closest Matches';
    }

    var html = '';

    html += '<div class="racket-quiz__results-icon" aria-hidden="true">'
      + '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">'
      + '<polyline points="20 6 9 17 4 12"/>'
      + '</svg>'
      + '</div>';
    html += '<h2 class="racket-quiz__results-heading">' + escapeHtml(heading) + '</h2>';
    html += '<p class="racket-quiz__results-text">' + escapeHtml(text) + '</p>';

    // Banner explaining what was relaxed (only for tiers 2-5)
    html += buildRelaxedBannerHTML(tier, relaxedKey);

    html += '<div class="racket-quiz__products-grid">';
    cards.forEach(function (product) {
      html += buildProductCardHTML(product);
    });
    html += '</div>';

    if (extras.length > 0) {
      var moreLabel = (tier && tier > 1)
        ? extras.length + ' more rackets also fit:'
        : extras.length + ' more rackets also fit your criteria:';
      html += '<div class="racket-quiz__more-list">';
      html += '<p class="racket-quiz__more-list-label">' + moreLabel + '</p>';
      html += '<ul class="racket-quiz__more-list-items">';
      extras.forEach(function (product) {
        html += '<li><a href="' + escapeAttr(product.url) + '">' + escapeHtml(product.title) + '</a></li>';
      });
      html += '</ul>';
      html += '</div>';
    }

    html += buildGuideLinkHTML();

    html += '<div class="racket-quiz__results-actions">';
    html += '<button class="racket-quiz__retake-btn" type="button" data-quiz-retake>'
      + '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">'
      + '<polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/>'
      + '</svg>'
      + 'Retake Quiz'
      + '</button>';
    html += '<a href="/collections/rackets" class="racket-quiz__browse-link">Browse All Rackets</a>';
    html += '</div>';

    return html;
  }

  function buildProductCardHTML(product) {
    var url = product.url || '/products/' + product.handle;
    var imageUrl = product.image || '';
    var hasImage = !!imageUrl;

    var price = formatMoney(product.price);
    var comparePrice = '';
    var onSale = false;
    if (product.compare_at_price && parseFloat(product.compare_at_price) > parseFloat(product.price)) {
      comparePrice = formatMoney(product.compare_at_price);
      onSale = true;
    }

    var html = '<a href="' + escapeAttr(url) + '" class="racket-quiz__product-card">';

    html += '<div class="racket-quiz__product-image-wrapper">';
    if (hasImage) {
      html += '<img class="racket-quiz__product-image" src="' + escapeAttr(imageUrl) + '" '
        + 'alt="' + escapeAttr(product.title) + '" loading="lazy" width="400" height="400" '
        + 'onerror="this.style.display=\'none\';this.nextElementSibling.style.display=\'flex\';">';
      html += '<div class="racket-quiz__product-image-placeholder" style="display:none;">'
        + '<svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" aria-hidden="true">'
        + '<rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>'
        + '</svg>'
        + '</div>';
    } else {
      html += '<div class="racket-quiz__product-image-placeholder">'
        + '<svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" aria-hidden="true">'
        + '<rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>'
        + '</svg>'
        + '</div>';
    }
    html += '</div>';

    html += '<div class="racket-quiz__product-info">';
    if (product.vendor) {
      html += '<div class="racket-quiz__product-vendor">' + escapeHtml(product.vendor) + '</div>';
    }
    html += '<div class="racket-quiz__product-title">' + escapeHtml(product.title) + '</div>';
    html += '<div class="racket-quiz__product-price' + (onSale ? ' racket-quiz__product-price--on-sale' : '') + '">';
    html += '<span class="racket-quiz__product-price-current">' + price + '</span>';
    if (onSale) {
      html += '<span class="racket-quiz__product-price-compare">' + comparePrice + '</span>';
    }
    html += '</div>';
    html += '<span class="racket-quiz__product-view-link">View Product <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="9 18 15 12 9 6"/></svg></span>';
    html += '</div>';

    html += '</a>';
    return html;
  }

  function buildNoResultsHTML(text) {
    var html = '';

    html += '<div class="racket-quiz__no-results">';
    html += '<div class="racket-quiz__no-results-icon" aria-hidden="true">'
      + '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">'
      + '<circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>'
      + '</svg>'
      + '</div>';
    html += '<p class="racket-quiz__no-results-text">' + escapeHtml(text || 'We couldn\'t find exact matches, but you can browse our full racket collection.') + '</p>';

    html += '<div class="racket-quiz__results-actions">';
    html += '<button class="racket-quiz__retake-btn" type="button" data-quiz-retake>'
      + '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">'
      + '<polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/>'
      + '</svg>'
      + 'Retake Quiz'
      + '</button>';
    html += '<a href="/collections/rackets" class="racket-quiz__browse-link">Browse All Rackets</a>';
    html += '</div>';

    html += '</div>';
    return html;
  }

  /* ------------------------------------------------
     Retake Quiz
     ------------------------------------------------ */
  function bindRetakeButton() {
    var btn = quiz.querySelector('[data-quiz-retake]');
    if (btn) {
      btn.addEventListener('click', function () {
        retakeQuiz();
      });
    }
  }

  function retakeQuiz() {
    answers = { level: null, style: null, shape: null, budget: null };
    budgetRange = { min: 0, max: 999999 };

    allOptions.forEach(function (opt) {
      opt.classList.remove('racket-quiz__option--selected');
      opt.setAttribute('aria-checked', 'false');
    });

    var firstStepOptions = quiz.querySelectorAll('[data-quiz-step="0"] [data-quiz-option]');
    firstStepOptions.forEach(function (opt, i) {
      opt.setAttribute('tabindex', i === 0 ? '0' : '-1');
    });

    resultsContainer.innerHTML = '';

    var activeStep = quiz.querySelector('.racket-quiz__step--active');
    if (activeStep) {
      activeStep.classList.remove('racket-quiz__step--active');
    }

    var firstStep = quiz.querySelector('[data-quiz-step="0"]');
    firstStep.classList.add('racket-quiz__step--active');

    currentStep = 0;
    isTransitioning = false;
    updateProgress();
    updateStepContainerHeight();

    var firstOption = firstStep.querySelector('[data-quiz-option]');
    if (firstOption) firstOption.focus();

    announceToScreenReader('Quiz restarted. Step 1 of 4.');

    quiz.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /* ------------------------------------------------
     Utilities
     ------------------------------------------------ */
  function escapeHtml(str) {
    if (str === null || str === undefined) return '';
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(String(str)));
    return div.innerHTML;
  }

  function escapeAttr(str) {
    if (str === null || str === undefined) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  /* ------------------------------------------------
     Boot
     ------------------------------------------------ */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
